import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { TriangleAlert, X } from "lucide-react";
import "./ConfirmationModal.css";

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  text: React.ReactNode;
  confirmButtonText?: string;
  cancelButtonText?: string;
  isDark?: boolean;
  size?: "md" | "lg";
  closeOnConfirm?: boolean;
  isLoading?: boolean;
  confirmDisabled?: boolean;
  icon?: React.ReactNode;
  /** "danger" styles the confirm button as a destructive action; the default stays "warning". */
  tone?: "warning" | "neutral" | "danger";
  /**
   * Opt-in layout. "inline-header" puts the icon and a one-line title in a fixed header row, makes the
   * description the only scrolling region and keeps the actions in a fixed footer. The default is unchanged.
   */
  layout?: "default" | "inline-header";
  /** Extra classes for the dialog panel, for caller-scoped sizing. */
  panelClassName?: string;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  text,
  confirmButtonText = "Confirm",
  cancelButtonText = "Cancel",
  isDark = false,
  size = "md",
  closeOnConfirm = true,
  isLoading = false,
  confirmDisabled = false,
  icon,
  tone = "warning",
  layout = "default",
  panelClassName,
}) => {
  const titleId = useId();
  const descriptionId = useId();
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  const loadingRef = useRef(isLoading);
  const submittingRef = useRef(false);
  closeRef.current = onClose;
  loadingRef.current = isLoading;

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        if (!loadingRef.current && !submittingRef.current) closeRef.current();
      }
      if (event.key === "Tab") {
        const controls = Array.from(panelRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]') || [])
          .filter((element) => !element.hidden && element.getAttribute("aria-hidden") !== "true");
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (!first) { event.preventDefault(); panelRef.current?.focus(); }
        else if (event.shiftKey && (document.activeElement === first || !panelRef.current?.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || !panelRef.current?.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    cancelButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const requestClose = () => {
    if (!isLoading && !submittingRef.current) onClose();
  };

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      requestClose();
    }
  };

  const handleConfirm = async () => {
    if (isLoading || confirmDisabled || submittingRef.current) return;
    submittingRef.current = true;
    try {
      await onConfirm();
      if (closeOnConfirm) onClose();
    } finally {
      submittingRef.current = false;
    }
  };

  const inline = layout === "inline-header";
  const iconTone = tone === "neutral" ? "confirmation-dialog-icon-neutral" : tone === "danger" ? "confirmation-dialog-icon-danger bg-red-50 ring-red-100" : "bg-amber-50 ring-amber-100";
  const iconNode = icon ?? <TriangleAlert size={24} className="text-amber-600" strokeWidth={2} aria-hidden="true" />;
  const closeButton = (
    <button
      type="button"
      onClick={requestClose}
      disabled={isLoading}
      aria-label="Close confirmation dialog"
      className={`confirmation-dialog-close ${inline ? "shrink-0" : "absolute right-4 top-4 z-10"} flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#7E57C2]/30 focus:ring-offset-2`}
    >
      <X size={18} strokeWidth={2} />
    </button>
  );
  const actions = (
    <>
      <button
        ref={cancelButtonRef}
        type="button"
        onClick={requestClose}
        disabled={isLoading}
        className="confirmation-dialog-cancel inline-flex min-h-11 w-full items-center justify-center whitespace-normal break-words rounded-xl border-[1px] border-gray-200 bg-white px-4 py-2.5 text-center text-sm font-medium leading-5 text-gray-700 shadow-sm transition-all hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#7E57C2]/20 focus:ring-offset-2 active:scale-[0.98]"
      >
        {cancelButtonText}
      </button>

      <button
        type="button"
        onClick={handleConfirm}
        disabled={isLoading || confirmDisabled}
        className={`confirmation-dialog-confirm ${tone === "danger" ? "is-danger bg-red-600 hover:bg-red-700" : "bg-[#7E57C2] hover:bg-[#6b46ac]"} inline-flex min-h-11 w-full items-center justify-center whitespace-normal break-words rounded-xl px-4 py-2.5 text-center text-sm font-medium leading-5 text-white shadow-sm transition-all hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#7E57C2]/30 focus:ring-offset-2 active:scale-[0.98]`}
      >
        {confirmButtonText}
      </button>
    </>
  );

  return createPortal(
    <div
      className={`confirmation-dialog-backdrop fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto bg-slate-950/50 px-3 py-4 backdrop-blur-sm sm:items-center sm:px-4 sm:py-6 ${isDark ? "dark" : ""}`}
      onMouseDown={handleBackdropClick}
      role="presentation"
    >
      <div
        role="dialog"
        ref={panelRef}
        tabIndex={-1}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className={`confirmation-dialog-panel dark-popup relative max-h-[calc(100vh-2rem)] w-full ${inline ? "confirmation-dialog-panel--inline-header flex flex-col overflow-hidden" : "overflow-y-auto"} rounded-2xl border-[1px] border-gray-200 bg-white shadow-2xl sm:max-h-[calc(100vh-3rem)] ${size === "lg" ? "max-w-2xl" : "max-w-md"}${panelClassName ? ` ${panelClassName}` : ""}`}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {inline ? (
          <>
            <div className="confirmation-dialog-header flex shrink-0 items-center gap-3">
              <div className={`confirmation-dialog-icon ${iconTone} flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1`}>
                {iconNode}
              </div>
              <h2 id={titleId} className="confirmation-dialog-title min-w-0 flex-1 font-poppins font-semibold text-gray-900">
                {title}
              </h2>
              {closeButton}
            </div>

            <div id={descriptionId} className="confirmation-dialog-description confirmation-dialog-body text-sm leading-6 text-gray-500">
              {text}
            </div>

            <div className="confirmation-dialog-footer grid shrink-0 grid-cols-1 gap-3 sm:grid-cols-2">
              {actions}
            </div>
          </>
        ) : (
          <>
            {closeButton}

            <div className="px-6 pb-6 pt-8 sm:px-8 sm:pb-8">
              <div className={`confirmation-dialog-icon ${iconTone} mb-5 flex h-12 w-12 items-center justify-center rounded-xl ring-1`}>
                {iconNode}
              </div>

              <h2
                id={titleId}
                className="pr-10 font-poppins text-xl font-semibold leading-7 text-gray-900"
              >
                {title}
              </h2>

              <div
                id={descriptionId}
                className="confirmation-dialog-description mt-2 text-sm leading-6 text-gray-500"
              >
                {text}
              </div>

              <div className="mt-7 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {actions}
              </div>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default ConfirmationModal;
